import { useScienceTutorSection } from './scienceTutorContext';
import { Calculator, CheckCircle2, Droplets } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ConcentrationVessel, DilutionExplore } from './SolutionsDilutionExplore';
import { type SolutionsView, ViewSwitch } from './SolutionsViewSwitch';
import { calculateDilution, checkDilutionAnswer } from './solutionsLab';

/** Practice: answer C₂ first; the working appears after Check. */
function DilutionPractice() {
  const [stockConcentration, setStockConcentration] = useState(1);
  const [aliquot, setAliquot] = useState(25);
  const [finalVolume, setFinalVolume] = useState(250);
  const [answer, setAnswer] = useState('');
  const [checked, setChecked] = useState(false);
  const [techniqueAnswer, setTechniqueAnswer] = useState('');
  const [techniqueChecked, setTechniqueChecked] = useState(false);
  useScienceTutorSection('SeniorMode', { stockConcentration, aliquot, finalVolume, answer, checked, techniqueAnswer, techniqueChecked });
  const result = useMemo(
    () =>
      calculateDilution({
        stockConcentrationMolL: stockConcentration,
        aliquotVolumeMl: aliquot,
        finalVolumeMl: finalVolume,
      }),
    [aliquot, finalVolume, stockConcentration],
  );
  const answerResult = checkDilutionAnswer(result.finalConcentrationMolL, Number(answer));
  const resetCheck = () => {
    setAnswer('');
    setChecked(false);
  };

  return (
    <article className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
      <div className="grid gap-8 xl:grid-cols-[340px_minmax(0,1fr)_330px]">
        <section>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            Choose the glassware values
          </p>
          <label className="mt-5 block text-sm font-extrabold text-ink" htmlFor="stock-concentration">
            Stock concentration{' '}
            <span className="text-brand-coral">{stockConcentration.toFixed(2)} mol L⁻¹</span>
          </label>
          <input
            id="stock-concentration"
            aria-label="Stock concentration"
            type="range"
            min="0.1"
            max="2"
            step="0.1"
            value={stockConcentration}
            onChange={(event) => {
              setStockConcentration(Number(event.target.value));
              resetCheck();
            }}
            className="mt-2 w-full accent-brand-coral"
          />

          <label className="mt-5 block text-sm font-extrabold text-ink" htmlFor="aliquot-volume">
            Pipette aliquot <span className="text-brand-sky">{aliquot} mL</span>
          </label>
          <input
            id="aliquot-volume"
            aria-label="Aliquot volume"
            type="range"
            min="10"
            max="100"
            step="5"
            value={aliquot}
            onChange={(event) => {
              const value = Number(event.target.value);
              setAliquot(value);
              if (finalVolume < value) setFinalVolume(value);
              resetCheck();
            }}
            className="mt-2 w-full accent-brand-sky"
          />

          <label className="mt-5 block text-sm font-extrabold text-ink" htmlFor="final-volume">
            Dilute to <span className="text-brand-mint">{finalVolume} mL</span>
          </label>
          <input
            id="final-volume"
            aria-label="Final flask volume"
            type="range"
            min={Math.max(100, aliquot)}
            max="500"
            step="25"
            value={finalVolume}
            onChange={(event) => {
              setFinalVolume(Number(event.target.value));
              resetCheck();
            }}
            className="mt-2 w-full accent-brand-mint"
          />
          <div className="mt-6 rounded-2xl bg-wash-sunshine p-4 text-sm font-medium leading-6 text-ink-soft">
            The pipette transfers solute and solution together. Adding water changes concentration,
            not the moles of solute transferred.
          </div>
        </section>

        <section className="min-w-0 rounded-[1.75rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-5 sm:p-6">
          <div className="grid min-w-0 grid-cols-1 items-center gap-4 sm:grid-cols-[1fr_auto_1fr] sm:gap-3">
            <ConcentrationVessel
              label="Measured aliquot"
              concentration={stockConcentration}
              volume={aliquot}
            />
            <div className="flex flex-col items-center gap-2 text-brand-sky">
              <Droplets className="h-8 w-8" aria-hidden="true" />
              <span className="text-xs font-extrabold">add water</span>
            </div>
            <ConcentrationVessel
              label="Volumetric flask"
              concentration={result.finalConcentrationMolL}
              volume={finalVolume}
              dilute
            />
          </div>
          <div className="mt-6 grid gap-3 rounded-2xl bg-white/80 p-4 text-sm font-medium text-ink-soft sm:grid-cols-2">
            <div>
              <span className="block text-xs font-extrabold uppercase tracking-wide text-slate2">
                Moles transferred
              </span>
              <span className="mt-1 block text-lg font-extrabold text-ink">
                {result.molesTransferred.toFixed(4)} mol
              </span>
            </div>
            <div>
              <span className="block text-xs font-extrabold uppercase tracking-wide text-slate2">
                Water added
              </span>
              <span className="mt-1 block text-lg font-extrabold text-ink">
                {result.waterAddedMl} mL
              </span>
            </div>
          </div>
          <p className="mt-4 text-xs font-medium leading-5 text-slate2">
            The dots show relative concentration, not the actual number or size of particles.
          </p>
        </section>

        <section className="min-w-0">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
            Calculate before revealing
          </p>
          <div className="mt-4 rounded-2xl border border-hairline bg-surface p-4 text-center">
            <p className="font-mono text-xl font-black text-ink">C₁V₁ = C₂V₂</p>
            <p className="mt-2 text-xs font-medium text-slate2">Use the same volume unit on both sides.</p>
          </div>
          <label className="mt-5 block text-sm font-extrabold text-ink" htmlFor="dilution-answer">
            Final concentration
          </label>
          <div className="mt-2 flex items-center gap-2 rounded-2xl border border-hairline bg-white px-4 py-3 focus-within:border-brand-sky">
            <input
              id="dilution-answer"
              aria-label="Calculated final concentration"
              type="number"
              min="0"
              step="0.001"
              value={answer}
              onChange={(event) => {
                setAnswer(event.target.value);
                setChecked(false);
              }}
              className="min-w-0 flex-1 bg-transparent text-lg font-extrabold text-ink outline-none"
              placeholder="0.000"
            />
            <span className="text-xs font-extrabold text-slate2">mol L⁻¹</span>
          </div>
          <button
            type="button"
            disabled={!answer}
            onClick={() => setChecked(true)}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Calculator className="h-4 w-4" aria-hidden="true" /> Check concentration
          </button>

          {checked ? (
            <div
              role="status"
              className={`mt-5 rounded-2xl border p-4 ${
                answerResult.correct
                  ? 'border-brand-mint/45 bg-wash-mint'
                  : 'border-brand-coral/45 bg-wash-coral'
              }`}
            >
              <p className="font-extrabold text-ink">
                {answerResult.correct ? 'Correct concentration' : 'Check the dilution ratio'}
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">
                C₂ = ({stockConcentration.toFixed(2)} × {aliquot}) ÷ {finalVolume} ={' '}
                <strong>{result.finalConcentrationMolL.toFixed(3)} mol L⁻¹</strong>
              </p>
              {!answerResult.correct ? (
                <p className="mt-2 text-xs font-medium leading-5 text-slate2">
                  Diluting to a larger final volume must make the concentration smaller than the stock.
                </p>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>

      <section className="mt-8 rounded-[1.75rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-white to-wash-mint p-5 sm:p-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)] lg:items-start">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
              Prepare it accurately
            </p>
            <h3 className="mt-2 text-xl font-extrabold text-ink">
              Which action gives the correct final volume?
            </h3>
            <div className="mt-4 grid gap-2" aria-label="Choose the correct volumetric technique">
              {[
                ['overfill', 'Fill above the mark, then remove the extra solution'],
                ['meniscus', 'At eye level, add the last drops until the bottom of the meniscus touches the mark'],
                ['above', 'Look down from above and stop when the top of the meniscus reaches the mark'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={techniqueAnswer === value}
                  onClick={() => {
                    setTechniqueAnswer(value);
                    setTechniqueChecked(false);
                  }}
                  className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold ${
                    techniqueAnswer === value
                      ? 'border-brand-sky bg-white shadow-card-soft'
                      : 'border-white bg-white/65'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={!techniqueAnswer}
              onClick={() => setTechniqueChecked(true)}
              className="mt-4 inline-flex items-center justify-center rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Check technique
            </button>
          </div>

          <div className="rounded-3xl border border-white bg-white/85 p-5 shadow-card-soft">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
              Volumetric flask sequence
            </p>
            <ol className="mt-4 grid gap-3 text-sm font-medium leading-6 text-ink-soft">
              {[
                ['1', 'Rinse and use a volumetric pipette to measure the aliquot.'],
                ['2', 'Transfer it to the flask; do not blow out a calibrated volumetric pipette.'],
                ['3', 'Add water close to the mark, then use a dropper for the final drops.'],
                ['4', 'Stopper and invert repeatedly so the concentration is uniform.'],
              ].map(([number, instruction]) => (
                <li key={number} className="grid grid-cols-[2rem_1fr] gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-wash-sunshine font-extrabold text-ink">
                    {number}
                  </span>
                  <span>{instruction}</span>
                </li>
              ))}
            </ol>
            {techniqueChecked ? (
              <div
                role="status"
                className={`mt-5 rounded-2xl border p-4 ${
                  techniqueAnswer === 'meniscus'
                    ? 'border-brand-mint/45 bg-wash-mint'
                    : 'border-brand-coral/45 bg-wash-coral'
                }`}
              >
                <p className="flex items-center gap-2 font-extrabold text-ink">
                  {techniqueAnswer === 'meniscus' ? (
                    <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" />
                  ) : null}
                  {techniqueAnswer === 'meniscus'
                    ? 'Correct volumetric technique'
                    : 'That would change the final concentration'}
                </p>
                <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">
                  {techniqueAnswer === 'overfill'
                    ? 'Removing liquid after overfilling also removes solute, so the prepared solution is no longer the intended dilution.'
                    : techniqueAnswer === 'above'
                      ? 'Looking from above causes parallax error. Bring the mark to eye level and read the bottom of the meniscus.'
                      : 'The mark defines the final solution volume. Eye-level reading avoids parallax, and the last drops prevent overfilling.'}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </article>
  );
}

/** Senior dilution: Explore (typed C₁, V₁, V₂ with C₂ = C₁ × V₁ ÷ V₂ always shown) or Practice. */
export function SeniorMode() {
  const [view, setView] = useState<SolutionsView>('explore');
  return (
    <div>
      <ViewSwitch view={view} onChange={setView} />
      {view === 'explore' ? <DilutionExplore /> : <DilutionPractice />}
    </div>
  );
}
